import React, { useEffect, useRef, useState } from 'react';
import { useWaterStore } from '../store/useWaterStore';
import { useDevStore } from '../store/useDevStore';
import { captureVideoFrame, detectDrinkingFromBase64, isDrinkingDetected } from '../utils/roboflowDetection';

export const CameraDetection: React.FC = () => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isVideoReady, setIsVideoReady] = useState(false);
  const [error, setError] = useState<string>('');
  const [detectionLog, setDetectionLog] = useState<string[]>([]);
  const { addWater, resetDaily, currentAmount, streak, updateStreak, checkAndUpdateStreak } = useWaterStore();
  const { isDevMode } = useDevStore();
  const lastDrinkTime = useRef<number>(0);
  const [isRoboflowProcessing, setIsRoboflowProcessing] = useState(false);
  const roboflowIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const [detectionThreshold, setDetectionThreshold] = useState(30); // Default threshold in normal mode
  const [showRainingEffect, setShowRainingEffect] = useState(true); // Option to disable raining effect
  const [detectionInterval, setDetectionInterval] = useState(5000); // Default interval in milliseconds

  // Store functions in refs to avoid dependency issues
  const addWaterRef = useRef(addWater);
  const streakRef = useRef(streak);
  const currentAmountRef = useRef(currentAmount);

  // Update refs when values change
  useEffect(() => {
    addWaterRef.current = addWater;
    streakRef.current = streak;
    currentAmountRef.current = currentAmount;
  }, [addWater, streak, currentAmount]);

  // Function to perform Roboflow detection
  const performRoboflowDetection = async () => {
    if (!videoRef.current || !isVideoReady || isRoboflowProcessing) return;

    try {
      setIsRoboflowProcessing(true);

      // Clear the canvas to remove any TensorFlow bounding boxes
      if (canvasRef.current) {
        const ctx = canvasRef.current.getContext('2d');
        if (ctx) {
          ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
        }
      }

      // Capture current frame from video
      const base64Image = captureVideoFrame(videoRef.current);

      // Send to Roboflow API
      const detectionResults = await detectDrinkingFromBase64(base64Image);

      // Log detection results
      const newLogs = [];
      let drinkingDetected = false;
      let drinkingConfidence = 0;

      if (detectionResults.predictions && detectionResults.predictions.length > 0) {
        // Find drinking prediction if any
        const drinkingPrediction = detectionResults.predictions.find((p: any) =>
          p.class?.toLowerCase() === "drinking" || p.class?.toLowerCase() === "drinking_glass"
        );

        if (drinkingPrediction) {
          drinkingConfidence = Math.round(drinkingPrediction.confidence * 100);
          if (drinkingConfidence >= detectionThreshold) {
            newLogs.push(`🍺 Drinking: ${drinkingConfidence}% ✅ (Above ${detectionThreshold}% threshold)`);
            drinkingDetected = true;
          } else {
            newLogs.push(`🍺 Drinking: ${drinkingConfidence}% ❌ (Below ${detectionThreshold}% threshold)`);
          }
        }

        // Add all other predictions
        detectionResults.predictions.forEach((prediction: any) => {
          if (prediction.class?.toLowerCase() !== "drinking" && prediction.class?.toLowerCase() !== "drinking_glass") {
            newLogs.push(`${prediction.class}: ${Math.round(prediction.confidence * 100)}%`);
          }
        });
      } else {
        newLogs.push('No objects detected');
      }

      // Check if drinking was detected based on current threshold
      if (drinkingDetected) {
        const now = Date.now();
        if (now - lastDrinkTime.current > 5000) { // 5-second cooldown
          console.log('Drinking detected by Roboflow, adding water.');
          addWaterRef.current(50); // Add a fixed amount, e.g., 50ml
          useWaterStore.getState().resetTimer(); // Reset the timer
          lastDrinkTime.current = now;

          // Add confirmation to logs
          newLogs.push(`💧 Added 50ml to your water intake! (${drinkingConfidence}% confidence)`);
          
          // Show raining effect if enabled
          if (showRainingEffect) {
            triggerRainingEffect();
          }
        }
      }

      setDetectionLog(newLogs);
    } catch (error) {
      console.error('Roboflow detection error:', error);
      setDetectionLog(['Roboflow API error']);
    } finally {
      setIsRoboflowProcessing(false);
    }
  };

  // Function to trigger raining effect
  const triggerRainingEffect = () => {
    // Create a temporary element for the effect
    const container = containerRef.current;
    if (!container) return;

    // Create raindrop elements
    for (let i = 0; i < 15; i++) {
      const drop = document.createElement('div');
      drop.className = 'absolute rounded-full bg-blue-400';
      drop.style.width = '8px';
      drop.style.height = '8px';
      drop.style.left = `${Math.random() * 100}%`;
      drop.style.top = '-10px';
      drop.style.opacity = '0.7';
      drop.style.animation = `rainDrop ${Math.random() * 0.5 + 0.5}s linear forwards`;
      
      container.appendChild(drop);
      
      // Remove the element after animation completes
      setTimeout(() => {
        if (drop.parentNode) {
          drop.parentNode.removeChild(drop);
        }
      }, 1000);
    }

    // Add CSS for the animation if not already present
    const styleId = 'rainDropStyle';
    if (!document.getElementById(styleId)) {
      const style = document.createElement('style');
      style.id = styleId;
      style.textContent = `
        @keyframes rainDrop {
          0% {
            transform: translateY(0) rotate(0deg);
            opacity: 0.7;
          }
          100% {
            transform: translateY(100px) rotate(360deg);
            opacity: 0;
          }
        }
      `;
      document.head.appendChild(style);
    }
  };

  useEffect(() => {
    let stream: MediaStream;

    const initCamera = async () => {
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: 'user',
            width: { ideal: 1280 },
            height: { ideal: 720 }
          },
          audio: false,
        });

        if (videoRef.current) {
          videoRef.current.srcObject = stream;
        }
        console.log('Camera stream initialized');
      } catch (err) {
        setError('Failed to initialize camera. Please make sure you have granted camera permissions.');
        console.error('Camera initialization error:', err);
      }
    };

    initCamera().then(() => {
      // Trigger initial detection
      setTimeout(() => {
        performRoboflowDetection();
      }, 500);
    });

    return () => {
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
      // Cleanup code
    };
  }, [resetDaily, updateStreak, checkAndUpdateStreak, isVideoReady]);

  // Effect to handle detection interval - runs in both normal and dev mode
  useEffect(() => {
    if (!isVideoReady || !videoRef.current) return;

    // Clear existing interval if any
    if (roboflowIntervalRef.current) {
      clearInterval(roboflowIntervalRef.current);
      roboflowIntervalRef.current = null;
    }

    // Set up new interval with current detectionInterval value
    roboflowIntervalRef.current = setInterval(() => {
      performRoboflowDetection();
    }, detectionInterval);

    return () => {
      if (roboflowIntervalRef.current) {
        clearInterval(roboflowIntervalRef.current);
        roboflowIntervalRef.current = null;
      }
    };
  }, [isVideoReady, detectionInterval]);

  const handleVideoLoad = () => {
    console.log('Video element loaded');
    setIsVideoReady(true);
  };

  return (
    <div className="relative w-full space-y-4" ref={containerRef}>
      {error ? (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-600">
          {error}
        </div>
      ) : (
        <>
          <div className="relative">
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              onLoadedData={handleVideoLoad}
              className="w-full rounded-lg shadow-lg"
            />
            <canvas
              ref={canvasRef}
              className="absolute top-0 left-0 w-full h-full pointer-events-none"
            />
            {/* Drinking Status Indicator */}
            <div className="absolute top-4 right-4 bg-black/50 text-white px-3 py-2 rounded-lg flex items-center">
              <span className="mr-2">Status:</span>
              {isRoboflowProcessing ? (
                <span className="flex items-center">
                  <span className="animate-pulse">🔍</span> Analyzing...
                </span>
              ) : detectionLog.some(log => log.includes('Drinking') && log.includes('✅')) ? (
                <span className="flex items-center text-green-300">
                  <span className="text-xl mr-1">💧</span> Drinking!
                </span>
              ) : (
                <span className="flex items-center">
                  <span className="text-xl mr-1">👀</span> Watching...
                </span>
              )}
            </div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow-lg mt-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-700">Detections</h3>
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-600">Mode:</span>
                <button
                  onClick={useDevStore.getState().toggleDevMode}
                  className={`px-3 py-1 rounded-full text-sm font-medium ${
                    isDevMode
                      ? 'bg-purple-100 text-purple-800 border border-purple-300'
                      : 'bg-blue-100 text-blue-800 border border-blue-300'
                  }`}
                >
                  {isDevMode ? '🔧 Dev Mode' : '💧 Normal Mode'}
                </button>
              </div>
            </div>
            <div className="mb-2">
              <div className={`text-sm font-medium px-2 py-1 rounded ${
                isDevMode ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800'
              }`}>
                <span className="mr-2">{isDevMode ? '🔧 Developer Mode' : '💧 Normal Mode'}</span>
                <span className="text-xs">Camera detection active</span>
              </div>
              <div className="mt-1 text-xs text-gray-500">
                {isRoboflowProcessing ? (
                  <span className="text-blue-500 animate-pulse">🔍 Processing cloud detection...</span>
                ) : (
                  <span>👀 Detection runs every 5 seconds. {isDevMode ? 'Viewing details, adjust threshold below.' : 'Automatically adding water with raining effect.'}</span>
                )}
              </div>
            </div>

            {/* Dev Mode Controls */}
            {isDevMode && (
              <div className="mb-4 p-3 bg-purple-50 rounded-lg border border-purple-200">
                <h4 className="font-medium text-purple-800 mb-2 text-sm">Developer Controls</h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  <div className="text-xs">
                    <label className="block font-medium text-gray-700 mb-1">
                      Threshold: {detectionThreshold}%
                    </label>
                    <input
                      type="range"
                      min="10"
                      max="90"
                      value={detectionThreshold}
                      onChange={(e) => setDetectionThreshold(parseInt(e.target.value))}
                      className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                    />
                    <div className="flex justify-between text-xs text-gray-500">
                      <span>10%</span>
                      <span>90%</span>
                    </div>
                  </div>
                  <div className="text-xs">
                    <label className="block font-medium text-gray-700 mb-1">
                      Raining Effect
                    </label>
                    <div className="flex items-center">
                      <label className="inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={showRainingEffect}
                          onChange={(e) => setShowRainingEffect(e.target.checked)}
                          className="sr-only peer"
                        />
                        <div className="relative w-9 h-5 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-blue-600"></div>
                        <span className="ms-2 text-xs font-medium text-gray-700">
                          {showRainingEffect ? 'On' : 'Off'}
                        </span>
                      </label>
                    </div>
                  </div>
                  <div className="text-xs">
                    <label className="block font-medium text-gray-700 mb-1">
                      Interval: {detectionInterval/1000}s
                    </label>
                    <input
                      type="range"
                      min="1000"
                      max="10000"
                      step="500"
                      value={detectionInterval}
                      onChange={(e) => setDetectionInterval(parseInt(e.target.value))}
                      className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                    />
                    <div className="flex justify-between text-xs text-gray-500">
                      <span>1s</span>
                      <span>10s</span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <ul className="list-disc pl-5 mb-4 max-h-40 overflow-y-auto">
              {detectionLog.map((log, index) => (
                <li key={index} className={`text-sm ${log.includes('✅') ? 'text-green-600 font-medium' : log.includes('❌') ? 'text-red-600' : 'text-gray-600'}`}>
                  {log.includes('Drinking') && log.includes('✅') && '💧 '}
                  {log.includes('Drinking') && log.includes('❌') && '❌ '}
                  {log}
                </li>
              ))}
            </ul>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={performRoboflowDetection}
                disabled={isRoboflowProcessing}
                className="bg-green-500 text-white py-2 px-3 rounded-lg shadow-md hover:bg-green-600 disabled:opacity-50 text-sm min-w-[120px]"
              >
                {isRoboflowProcessing ? '🔍 Processing...' : '💧 Manual Detect'}
              </button>
              <button
                onClick={() => setDetectionThreshold(30)}
                className="bg-gray-200 text-gray-700 py-2 px-3 rounded-lg shadow-md hover:bg-gray-300 text-sm min-w-[120px]"
              >
                Reset Threshold
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};